<template>
  <UiDialog @update:open="onUpdateOpen">
    <UiDialogContent
      class="max-w-[900px] overflow-x-auto bg-background-secondary p-0 transition-none md:overflow-hidden"
      :class="{
        'pointer-events-none opacity-50': busy,
      }"
    >
      <div class="flex h-full">
        <DialogGoLiveDestinationsStep
          @cancel="() => emit('cancel')"
          class="max-h-screen min-w-[400px] max-w-[400px] overflow-y-auto p-6"
        />
        <div class="flex max-h-screen w-full flex-col border-l">
          <div
            class="relative h-[525px] w-[500px] flex-grow overflow-auto border-b py-8 pl-4 pr-8"
          >
            <Form v-if="fieldsets" :fieldsets="fieldsets" use-accordion />
            <TwitterShareStreamBox class="my-4" v-if="hasEnabledDestinations" />
            <div
              class="h-full w-full px-4 pb-4 pt-12"
              v-else-if="!fieldsets && !hasEnabledDestinations"
            >
              <DialogGoLiveEmpty />
            </div>
          </div>
          <div
            class="flex min-h-[90px] w-full justify-end overflow-hidden border-t p-6"
          >
            <Button :busy="busy" @click="startStreaming" variant="primary">
              {{ $t('Confirm & Go Live') }}
            </Button>
          </div>
        </div>
      </div>
    </UiDialogContent>
  </UiDialog>
</template>

<script setup lang="ts">
import { generateSignedUrl, updateRstUserProfile } from '~/lib/streamlabs-api';
import Bindings, { openBrowserTo, setStreamSettings } from '~/lib/bindings';
import { decrypt } from '~/lib/security';
import { sleep } from '~/lib/util';
import { getInitialValues } from '~/lib/forms';
import { RstToPlatformType } from '~/stores/multistream.store';
import { useToast } from '~/components/ui/toast';
import UiButton from '~/components/ui/button/Button.vue';

const { t: $t } = useI18n();
const multistream = useMultistreamStore();
const app = useAppStore();
const dialogs = useDialogsStore();
const auth = useAuthStore();
const api = useApiStore();
const obs = useObsStore();
const goLive = useGoLiveStore();
const chat = useChatStore();
const { toast } = useToast();
const { track: $track } = useTrackingStore();

const ytInfo = api.golive.youtube.info;
const rstUserProfile = api.multistream.user.profile;
const busy = ref(false);
const emit = defineEmits(['cancel']);

// refresh youtube info on enable
watch(
  () => multistream.isYouTubeEnabled,
  (is) => is && ytInfo.refresh(),
);

const { fieldsets, form } = useGoLiveForm();
const hasEnabledDestinations = computed(() => {
  return multistream.enabledDestinations.length > 0;
});

const getPlatformSettingsPayload = (
  values: {
    [x: string]: any;
  },
  platform: RstToPlatformType,
) => {
  switch (platform) {
    case 'twitch':
      return {
        title: values.twitch_title,
        category: values.twitch_category,
        tags: values.twitch_tags.length
          ? values.twitch_tags.join(',')
          : undefined,
        branded: values.twitch_branded_content,
        // TODO: plz make backend take an array
        classifications: values.twitch_content_classification.join(','),
      };
    case 'kick':
      return {
        title: values.kick_title,
        category: values.kick_category,
      };
    case 'tiktok':
      return {
        title: values.tiktok_title,
        category: values.tiktok_category,
        audience_type: values.tiktok_audience_type,
      };
    case 'youtube':
      return {
        title: values.youtube_title,
        privacy: values.youtube_privacy,
        category: values.youtube_category,
        description: values.youtube_description,
        auto_start: values.youtube_auto_start,
        auto_stop: values.youtube_auto_stop,
        dvr: values.youtube_dvr,
        for_kids: values.youtube_for_kids,
        latency: values.youtube_latency,
        projection: values.youtube_360_video ? '360' : 'rectangular',
        broadcast_id: values.youtube_broadcast_id,
        thumbnail: values.youtube_thumbnail,
      };
    case 'facebook':
      return {
        title: values.facebook_title,
        description: values.facebook_description,
        // This could be a page id or the user's ID to stream to timeline
        page_id: values.facebook_destination,
      };
    case 'twitter':
      return {
        title: values.twitter_title,
      };
    case 'trovo':
      return {
        title: values.trovo_title,
      };
    case 'patreon':
      return {
        title: values.patreon_title,
        description: values.patreon_description,
        access_rules: values.patreon_access_rules,
      };
  }

  return {};
};

const startStreaming = async () => {
  await form.handleSubmit(async (values) => {
    $track('going_live_via_plugin_attempted', {
      type:
        multistream.enabledDestinations.length > 1 ? 'multistream' : 'basic',
      platforms: multistream.enabledDestinations
        .filter((d) => d.type === 'platform')
        .map((d) => d.platform_type),
    });

    busy.value = true;

    if (values.youtube_thumbnail instanceof File) {
      // ... upload to s3 and replace with url
      const { data } = await generateSignedUrl({
        type: 'youtube-thumbnail',
        extension: 'png',
      });

      const formData = new FormData();

      for (const key in data.value?.inputs) {
        if (data.value.inputs[key]) {
          formData.append(key, data.value.inputs[key]);
        }
      }

      formData.append('file', values.youtube_thumbnail);

      if (!data.value?.attributes?.action) return;
      await useFetch(data.value.attributes.action, {
        method: 'POST',
        body: formData,
      });

      values.youtube_thumbnail = {
        path: data.value.path,
      };
    } else {
      // otherwise don't supply it, it's already uploaded
      values.youtube_thumbnail = undefined;
    }

    let chatDockUrl;

    if (multistream.enabledDestinations.length === 1) {
      const destination = multistream.enabledDestinations[0];

      switch (destination?.type) {
        case 'platform': {
          const payload = getPlatformSettingsPayload(
            values,
            destination.platform_type,
          );

          const response = await goLive
            .startBroadcast(destination.platform_type, payload)
            .catch(() => {
              // Code below incorrectly assumes this call will always succeed
              // Until backend provides more information we fake a response
              // as otherwise results in unhandled promise rejection
              // TODO: type properly if we keep this approach
              return { error: true } as any;
            });

          if (
            !response.data?.rtmp ||
            !response.data?.key ||
            !auth.authResponseParams
          ) {
            console.error(
              `!response.data.value.rtmp || !response.data.value.key || !auth.authResponseParams`,
            );

            const destinationName =
              multistream.platformConfigs[destination.platform_type].label;

            const { dismiss } = toast({
              duration: 60000,
              title: 'Failed to Start Stream',
              description: `We were unable to fetch a valid streaming key. If issues persist, please reconnect and try again.`,
              variant: 'destructive',
              action: h(
                'div',
                {
                  class: 'space-x-2 flex justify-end w-full',
                },
                [
                  h(
                    UiButton,
                    {
                      variant: 'link',
                      class: 'text-white/70',
                      size: 'sm',
                      altText: 'Dismiss',
                      onClick: () => {
                        dismiss();
                      },
                    },
                    {
                      default: () => 'Dismiss',
                    },
                  ),

                  h(
                    UiButton,
                    {
                      variant: 'destructive',
                      class: '!border-white/50 border text-white/90',
                      size: 'sm',
                      altText: `Reconnect ${destinationName}`,
                      onClick: () => {
                        multistream.connectPlatform(
                          PlatformTypeToRst[destination.platform_type],
                        );

                        dismiss();
                      },
                    },
                    {
                      default: () => `Reconnect ${destinationName}`,
                    },
                  ),
                ],
              ),
            });

            busy.value = false;
            return;
          }

          const streamKey = await decrypt(
            response.data.key,
            auth.authResponseParams.v,
          );

          await setStreamSettings({
            key: streamKey,
            url: response.data.rtmp,
          });

          if (destination.platform_type === RstToPlatformType.trovo_account) {
            chatDockUrl = `https://trovo.live/chat/${response.data.channel_name}`;
          }

          if (response.data.chat_url) {
            const url = new URL(
              response.data.chat_url.replace(/https:\/\/?/, 'https://'),
            );

            if (url.host.endsWith('twitch.tv')) {
              url.searchParams.set('darkpopout', '');
            }

            chatDockUrl = url.toString();

            // FIXME: backend fix for these two urls (Trovo above was straight up missing)
            // in addition to YouTube.
            // `parent` is fixed on pages/docks/chat.vue, as well as YouTube's `embed_domain`
            if (destination.platform_type === 'twitch') {
              chatDockUrl = `https://www.twitch.tv/popout/${response.data.channel_name}/chat?darkpopout`;
            }

            if (destination.platform_type === 'tiktok') {
              chatDockUrl = `${window.location.origin}/docks/tiktok-chat`;
              openTiktokLiveMonitor();
            }
          }

          const liveInfo: DestinationPlatformLiveInfo =
            {} as DestinationPlatformLiveInfo;
          // TODO: type asserts
          if (destination.platform_type === RstToPlatformType.youtube_account) {
            (liveInfo as YouTubeLiveInfo).broadcastId =
              response.data.broadcast_id;
          }
          goLive.setAndBroadcastStreamingStatus(true, [
            { ...destination, liveInfo },
          ]);
          break;
        }
        case 'relay': {
          const relay = destination;
          await setStreamSettings({
            key: relay.streamKey,
            url: relay.streamUrl,
          });
          goLive.setAndBroadcastStreamingStatus(true);
          break;
        }
      }
    } else {
      if (
        !multistream.canMultistreamWithoutUltra &&
        !auth.ensureUltra('go_live_multiple_destinations', {
          refl: 'obsplugin-multistream',
        })
      ) {
        busy.value = false;
        return;
      }

      if (!rstUserProfile.data) {
        console.debug(`!rstUserProfile`, rstUserProfile);
        return;
      }

      rstUserProfile.data.title = values.title;
      rstUserProfile.data.description = values.description;

      const multistreamChatPlatforms: MultistreamChatSupportedPlatform[] = [];

      for (const [k, v] of Object.entries(RstToPlatformType) as Entries<
        typeof RstToPlatformType
      >) {
        const enabled = multistream.enabledDestinations.some(
          (destination) =>
            destination.type === 'platform' && destination.platform_type === v,
        );

        rstUserProfile.data[k] = enabled;

        if (enabled) {
          multistreamChatPlatforms.push(v);
          rstUserProfile.data[v] = getPlatformSettingsPayload(values, v);
        } else {
          delete rstUserProfile.data[v];
        }
      }

      const [
        {
          data: { value },
        },
        {
          data: { server: streamUrl },
        },
      ] = await Promise.all([
        multistream.enableMultistream(),
        api.get<RstIngestResponse>(`/api/v5/obs-plugin/rst/ingest`),
        updateRstUserProfile(rstUserProfile.data),
      ]);

      if (!value?.streamKey) return;
      await setStreamSettings({
        key: value.streamKey,
        url: streamUrl,
      });

      rstUserProfile.refresh();

      if (
        multistream.enabledDestinations.some(
          (destination) =>
            destination.type === 'platform' &&
            destination.platform_type === 'tiktok',
        )
      ) {
        /* Opening the link too early results in TikTok error on that page
         * presumably because the stream isn't live yet when going through
         * multistream
         */
        sleep(5000).then(openTiktokLiveMonitor);
      }

      chatDockUrl = auth.getMultistreamChatUrl(multistreamChatPlatforms);
    }

    if (chatDockUrl && obs.getDockByTitle('Streamlabs: Chat')) {
      console.debug('Setting chatdock url to', chatDockUrl);
      chat.setChatUrl(chatDockUrl);
    }

    obs.bringToFront();
    Bindings.qt.click_stream_button();
    $track('going_live_via_plugin_started', {
      type:
        multistream.enabledDestinations.length > 1 ? 'multistream' : 'basic',
      platforms: multistream.enabledDestinations
        .filter((d) => d.type === 'platform')
        .map((d) => d.platform_type),
    });
    emit('cancel');
    busy.value = false;
  })();
};

const openTiktokLiveMonitor = () => {
  console.groupCollapsed('opening tiktok monitor');
  api.golive.tiktok.links
    .ensure()
    .then(({ monitor: monitorUrl }) => {
      console.debug({ monitorUrl });
      if (monitorUrl) {
        // open the tiktok monitor
        openBrowserTo(monitorUrl);
      }
    })
    .finally(() => {
      console.groupEnd();
    });
};

const hasSeenGoLiveFlow = ref<boolean>();

const onUpdateOpen = async (open: boolean) => {
  if (!open) {
    emit('cancel');
  }
};

onMounted(() => {
  hasSeenGoLiveFlow.value = app.flags.hasSeenGoLiveFlow;
  app.setFlag('hasSeenGoLiveFlow', true);

  /*
   * TODO: this field setting, plus the watching below, although dirty,
   * it's way cleaner than diving into Form, FormSet, vee-validate functions
   * outside our control, and more, believe me, I tried. Contributions welcome.
   */
  form.setFieldValue('title', goLive.title);
  form.setFieldValue('description', goLive.description);
});

watch(
  () => form.values.title,
  (newVal) => goLive.updateTitle(newVal),
);

watch(
  () => form.values.description,
  (newVal) => goLive.updateDescription(newVal),
);

watch(
  () => multistream.enabledDestinations,
  (is) => {
    form.setFieldValue('destinationCount', is.length, false);
  },
  { immediate: true },
);

watch(
  fieldsets,
  (is) => {
    if (!is) return;

    form.setValues(getInitialValues(is), false);
  },
  { deep: true },
);
</script>
